// The signed-in gallery, backed by D1 (Block I, job 4).
//
// Deliberately shaped like src/gallery.js — same entry objects, same
// add/remove/subscribe surface — because useGallery swaps between the two and
// nothing downstream should be able to tell which one it is talking to.
//
// DECIDED (2026-07-26, owner): once you are logged in, **D1 is the only truth.**
// localStorage is not a cache of it and not reconciled with it; it remains the
// anonymous store, for logged-out use and as the untouched backup below. A save
// that cannot reach the server FAILS LOUDLY through the error the placard
// already shows, rather than landing somewhere that quietly disagrees with the
// server later.
//
// State lives at MODULE scope on purpose: four components call useGallery(), and
// per-hook state would mean four GETs on load and four copies that drift apart
// the moment one of them saved something.

import coordId from './coordId';
import { listEntries } from './gallery';
import * as api from './api';

// Which browsers have already handed their anonymous gallery to which account.
// NOT a record of what was migrated — just that it happened, so a returning user
// doesn't POST their whole local gallery on every single load.
const MIGRATED_KEY = 'fractalart.gallery.migrated.v1';

let session = null;                                    // { sub, getToken }
let state = { status: 'idle', entries: [], error: null };
let inflight = null;
const listeners = new Set();

const emit = () => listeners.forEach(listener => listener());

const setState = (next) => {
    state = { ...state, ...next };
    emit();
};

export const getState = () => state;

export const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

// ---- the migration marker ---------------------------------------------------
// Every localStorage touch is wrapped: a blocked or corrupt store must degrade to
// "migrate again" (harmless — the server dedupes) rather than throw on login.

const readMarkers = () => {
    try {
        const parsed = JSON.parse(window.localStorage.getItem(MIGRATED_KEY) || '{}');
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const hasMigrated = (sub) => Boolean(readMarkers()[sub]);

const markMigrated = (sub) => {
    try {
        window.localStorage.setItem(MIGRATED_KEY, JSON.stringify({
            ...readMarkers(),
            [sub]: new Date().toISOString()
        }));
    } catch {
        /* unmarked means we re-POST next login; the server ignores duplicates */
    }
};

/**
 * Hand this browser's anonymous gallery to the account that just signed in.
 *
 * DECIDED (2026-07-26, owner): this happens **silently**, with no prompt — the
 * overwhelmingly common case is one person on their own machine, and asking
 * would put a dialog between someone and the specimens they already believe are
 * theirs. The known cost is the shared-browser case: whoever logs in on a
 * machine inherits whatever was saved anonymously on it. Accepted knowingly.
 *
 * Two invariants, both from Block I's sharp edges:
 *  - **Idempotent.** The server's primary key is (user, coordId) with ON CONFLICT
 *    DO NOTHING, so a reload mid-migration, a retry, or the same browser signing
 *    in on two devices cannot double-insert. That works because Block H made the
 *    client RECOMPUTE `id` from the bounds on every read — client and server
 *    derive the same key from the same numbers.
 *  - **localStorage is never drained.** Not on success, not ever. If a migration
 *    half-failed after clearing it, the entries would be gone — and unlike most
 *    data loss, a deep zoom cannot be found again by hand, which is the entire
 *    reason the gallery exists.
 */
const migrateLocalGallery = async (sub, token) => {
    if (hasMigrated(sub)) return;

    const local = listEntries();
    if (local.length === 0) {
        markMigrated(sub);   // nothing to move, but don't come back and re-check
        return;
    }

    await api.saveEntries(token, local);
    markMigrated(sub);
};

// ---- loading ----------------------------------------------------------------

const loadNow = async () => {
    const { sub, getToken } = session;
    const token = await getToken();

    // Migration first, so the very first render of a newly signed-in gallery
    // already contains the entries the user arrived with. Fetching first would
    // show them an empty gallery and then pop their specimens in afterwards.
    await migrateLocalGallery(sub, token);

    const payload = await api.fetchGallery(token);
    return Array.isArray(payload?.entries) ? payload.entries : [];
};

// Single-flight: four hooks mounting at once produce ONE request.
const load = () => {
    if (inflight) return inflight;

    setState({ status: 'loading', error: null });
    inflight = loadNow()
        .then((entries) => {
            setState({ status: 'ready', entries, error: null });
            return entries;
        })
        .catch((error) => {
            console.error('[gallery] could not load from the server', error);
            // NOT falling back to localStorage. A logged-in user seeing their old
            // anonymous gallery under the impression it is their account's would
            // be a worse failure than an honest error.
            setState({ status: 'error', entries: [], error });
            return [];
        })
        .finally(() => { inflight = null; });

    return inflight;
};

/** Called by useGallery when Auth0 settles. Re-loads only when the identity changes. */
export const setSession = (sub, getToken) => {
    if (session?.sub === sub) {
        session = { sub, getToken };   // keep the freshest token getter
        return;
    }
    session = { sub, getToken };
    state = { status: 'loading', entries: [], error: null };
    inflight = null;
    load();
};

export const clearSession = () => {
    if (!session) return;
    session = null;
    inflight = null;
    setState({ status: 'idle', entries: [], error: null });
};

/** Retry after a failed load — the gallery view offers this rather than a dead end. */
export const reload = () => (session ? load() : Promise.resolve([]));

// ---- mutations --------------------------------------------------------------
// Optimistic, then confirmed. Block H's note stands: the Save mark must not wait
// on anything, and it now has a network round trip behind it rather than a
// synchronous render. So the mark flips immediately and REVERTS if the server
// refuses — which is the same contract the local store has, just with a gap in
// the middle.

const byNewestFirst = (a, b) => (a.savedAt < b.savedAt ? 1 : a.savedAt > b.savedAt ? -1 : 0);

export const addEntry = async (bounds, { title = null, savedAt } = {}) => {
    if (!session) return { ok: false, id: null };

    const entry = {
        id: coordId(bounds),
        realMin: bounds.realMin,
        realMax: bounds.realMax,
        imaginaryMin: bounds.imaginaryMin,
        imaginaryMax: bounds.imaginaryMax,
        savedAt: savedAt || new Date().toISOString(),
        title
    };

    if (state.entries.some(e => e.id === entry.id)) {
        return { ok: true, id: entry.id, alreadySaved: true };
    }

    const previous = state.entries;
    setState({ entries: [entry, ...previous].sort(byNewestFirst) });

    try {
        await api.saveEntries(await session.getToken(), [entry]);
        return { ok: true, id: entry.id };
    } catch (error) {
        console.error('[gallery] save failed', error);
        setState({ entries: previous, error });
        return { ok: false, id: entry.id };
    }
};

export const removeEntry = async (id) => {
    if (!session) return false;

    const previous = state.entries;
    const remaining = previous.filter(e => e.id !== id);
    if (remaining.length === previous.length) return true;   // nothing to do

    setState({ entries: remaining });

    try {
        await api.deleteEntry(await session.getToken(), id);
        return true;
    } catch (error) {
        console.error('[gallery] remove failed', error);
        setState({ entries: previous, error });
        return false;
    }
};

// Test seam. Module-scope state outlives a test file otherwise, and a store that
// remembers the previous test's session is a very confusing way to lose an hour.
export const __resetForTests = () => {
    session = null;
    inflight = null;
    state = { status: 'idle', entries: [], error: null };
    listeners.clear();
};
